import React, { useState } from "react";
import { Card, Form, Row, Col, Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Block from "../../components/Block/Block";
import { Icon } from "../../components";
import { createDriverApi } from "../../services/UserService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const DriverAddPage = () => {
  const emptyFormData = {
    first_name: "",
    last_name: "",
    email: "",
    mobile_number: "",
    password: "",
    dl_number: "",
  };

  const [formData, setFormData] = useState(emptyFormData);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const navigate = useNavigate();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    const formattedValue = name === "dl_number" ? value.toUpperCase() : value;

    setFormData({ ...formData, [name]: formattedValue });
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handlePasswordToggle = () => {
    setPasswordVisible(!passwordVisible);
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileNumberRegex = /^[0-9]{10}$/;
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%?&])[A-Za-z\d@$!%?&]{8,}$/;

    if (!formData.first_name) newErrors.first_name = "First name is required";
    if (!formData.last_name) newErrors.last_name = "Last name is required";
    
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    
    if (!formData.mobile_number) {
      newErrors.mobile_number = "Mobile Number is required";
    } else if (!mobileNumberRegex.test(formData.mobile_number)) {
      newErrors.mobile_number = "Mobile Number must be 10 digits.";
    }
    
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!passwordRegex.test(formData.password)) {
      newErrors.password = "Password must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character.";
    }
    
    if (!formData.dl_number) newErrors.dl_number = "DL number is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmitLoading(true);
      const response = await createDriverApi(formData);
      setErrors({});
      toast.success(response?.data?.message || "Driver created successfully!");
      setTimeout(() => navigate("/drivers"), 1500);
    } catch (error) {
      const backendErrors = error.response?.data;
      
      if (backendErrors) {
        const fieldErrors = {};
        
        // Handle field-specific errors from backend
        if (backendErrors.email) {
          fieldErrors.email = Array.isArray(backendErrors.email) ? backendErrors.email[0] : backendErrors.email;
        }
        if (backendErrors.mobile_number) {
          fieldErrors.mobile_number = Array.isArray(backendErrors.mobile_number) ? backendErrors.mobile_number[0] : backendErrors.mobile_number;
        }
        if (backendErrors.dl_number) {
          fieldErrors.dl_number = Array.isArray(backendErrors.dl_number) ? backendErrors.dl_number[0] : backendErrors.dl_number;
        }
        
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
        } else {
          // Handle non-field errors
          toast.error(backendErrors.message || "An error occurred while creating driver");
        }
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData(emptyFormData);
    setErrors({});
    navigate("/drivers");
  };

  return (
    <div>
      <Block.Head>
        <Block.HeadBetween>
          <Block.HeadContent>
            <Block.Title tag="h2">Add New Driver</Block.Title>
            <nav>
              <ol className="breadcrumb breadcrumb-arrow mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/drivers">Drivers</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Add Driver
                </li>
              </ol>
            </nav>
          </Block.HeadContent>
        </Block.HeadBetween>
      </Block.Head>

      <Block>
        <Form onSubmit={handleSubmit}>
          <Row className="g-gs">
            <Col xxl="12">
              <div className="gap gy-4">
                <div className="gap-col">
                  <Card className="card-gutter-md">
                    <Card.Body>
                      <h4 className="bio-block-title mb-4">Enter Driver Details</h4>
                      <Row className="g-gs">
                        {/* Name Fields */}
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              type="text"
                              name="first_name"
                              placeholder="Enter First Name"
                              value={formData.first_name}
                              onChange={handleChange}
                              isInvalid={!!errors.first_name}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.first_name}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              type="text"
                              name="last_name"
                              placeholder="Enter Last Name"
                              value={formData.last_name}
                              onChange={handleChange}
                              isInvalid={!!errors.last_name}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.last_name}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        {/* Email */}
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              type="email"
                              name="email"
                              placeholder="Enter Email"
                              value={formData.email}
                              onChange={handleChange}
                              isInvalid={!!errors.email}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.email}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        {/* Phone Number */}
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Mobile Number <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              type="text"
                              name="mobile_number"
                              placeholder="Enter Phone Number"
                              value={formData.mobile_number}
                              onChange={handleChange}
                              isInvalid={!!errors.mobile_number}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.mobile_number}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        {/* DL Number */}
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>DL Number <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              type="text"
                              name="dl_number"
                              placeholder="Enter DL number"
                              value={formData.dl_number}
                              onChange={handleChange}
                              style={{ textTransform: "uppercase" }}
                              isInvalid={!!errors.dl_number}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.dl_number}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        {/* Password */}
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                            <div className="position-relative">
                              <Form.Control
                                type={passwordVisible ? "text" : "password"}
                                name="password"
                                placeholder="Enter Password"
                                value={formData.password}
                                onChange={handleChange}
                                isInvalid={!!errors.password}
                                autoComplete="new-password"
                                className="pe-5"
                              />
                              <span
                                onClick={handlePasswordToggle}
                                className="position-absolute end-0 top-50 translate-middle-y me-2"
                                style={{ cursor: "pointer" }}
                              >
                                <Icon name={passwordVisible ? "eye-alt-fill" : "eye-off-fill"} />
                              </span>
                              <Form.Control.Feedback type="invalid">
                                {errors.password}
                              </Form.Control.Feedback>
                            </div>
                          </Form.Group>
                        </Col>

                        {/* Buttons */}
                        <div className="d-flex justify-content-center mt-4">
                          <Button
                            variant="primary"
                            type="submit"
                            disabled={isSubmitLoading}
                            className="me-3"
                          >
                            {isSubmitLoading ? "Submitting..." : "Submit"}
                          </Button>
                          <Button variant="secondary" onClick={handleCancel}>
                            Cancel
                          </Button>
                        </div>
                      </Row>
                    </Card.Body>
                  </Card>
                </div>
              </div>
            </Col>
          </Row>
        </Form>
      </Block>
    </div>
  );
};

export default DriverAddPage;